/**
 * useAppI18n — App-level i18n hook for IdeaWeave custom UI.
 *
 * Reads from `appLangCodeAtom` (app-jotai) so it works everywhere —
 * including outside the <Excalidraw> tree (Entry screen, UserAuthModal,
 * ProjectShelf, AIWeaverSidebar, etc.).
 *
 * Usage:
 *   const { t, langCode } = useAppI18n();
 *   <span>{t("entry.sketchIdea")}</span>
 */

import { useAtomValue } from "../app-jotai";
import { appLangCodeAtom } from "../app-language/language-state";

import enData from "../locales/en.json";
import zhCNData from "../locales/zh-CN.json";

type LocaleData = typeof enData;

const localeMap: Record<string, LocaleData> = {
  en: enData,
  "zh-CN": zhCNData,
};

/**
 * Resolve a dotted key path like "entry.sketchIdea" from a nested object.
 */
const resolve = (
  data: Record<string, any>,
  path: string,
): string | undefined => {
  const parts = path.split(".");
  let current: any = data;
  for (const part of parts) {
    if (current == null || typeof current !== "object") {
      return undefined;
    }
    current = current[part];
  }
  return typeof current === "string" ? current : undefined;
};

/**
 * Pick the best locale data for a given langCode.
 * - Exact match first (e.g. "zh-CN")
 * - Prefix match (e.g. "zh-TW" → "zh-CN")
 * - Fallback to English
 */
const pickLocale = (langCode: string): LocaleData => {
  if (localeMap[langCode]) {
    return localeMap[langCode];
  }
  // Try prefix match: "zh-TW" → "zh", find first key starting with "zh"
  const prefix = langCode.split("-")[0];
  for (const key of Object.keys(localeMap)) {
    if (key === prefix || key.startsWith(`${prefix}-`)) {
      return localeMap[key];
    }
  }
  return enData;
};

export const useAppI18n = () => {
  const langCode = useAtomValue(appLangCodeAtom);
  const data = pickLocale(langCode);

  const t = (
    key: string,
    replacement?: Record<string, string | number>,
  ): string => {
    let text = resolve(data, key) ?? resolve(enData, key) ?? key;
    if (replacement) {
      for (const k of Object.keys(replacement)) {
        text = text.replace(`{{${k}}}`, String(replacement[k]));
      }
    }
    return text;
  };

  return { t, langCode };
};
